/**
 * Background colour crossfade functionality
 *
 * This function creates a smooth background color transition as the user scrolls
 * through different impact sections. The background color changes based on which
 * section is currently visible in the viewport.
 *
 * @param {jQuery} $ - jQuery reference (passed from WordPress)
 * @param {jQuery} $entryContent - The main content element whose background will change
 */
function initBackgroundCrossfade($, $entryContent) {
	// Early return if no content element found
	if (!$entryContent.length) return;

	// Define the 4 colors that cycle through sections (RGB values for smooth interpolation)
	// These correspond to the Less variables in _colors.less
	// If you change this order, also change _block-colors.less
	// If you change these colour values, also change _colors.less
	const colors = [
		{ r: 223, g: 231, b: 247 }, // #DFE7F7 - @impact-ocean-50
		{ r: 232, g: 238, b: 238 }, // #E8EEEE - @impact-forest-50
		{ r: 244, g: 248, b: 234 }, // #F4F8EA - @impact-lime-50
		{ r: 246, g: 245, b: 239 }  // #F6F5EF - @impact-sand-50
	];

	// Performance optimization: throttle scroll events to prevent excessive function calls
	// This ensures smooth 60fps updates without overwhelming the browser
	let ticking = false;

	// Cache the last applied color to avoid unnecessary DOM updates
	let lastAppliedColor = null;

	// Cache the last active navigation index to avoid unnecessary DOM updates
	let lastActiveNavIndex = -1;

	/**
	 * Updates the navigation menu to highlight the currently visible section
	 * Only updates when the active section actually changes (performance optimization)
	 *
	 * @param {number} currentSectionIndex - Index of the section currently in view (0-based)
	 */
	function updateNavigationActiveState(currentSectionIndex) {
		// Only update if the active section has actually changed
		if (currentSectionIndex === lastActiveNavIndex) { return; }

		// Find all navigation menu items (li elements in the sub-menu)
		const $navItems = $('#nav-impact .sub-menu > li');
		if (!$navItems.length)  { return; }

		// Remove active state from all navigation links
		// This ensures only one item is highlighted at a time
		$navItems.find('a').removeClass('active');

		// Add active state to the navigation item corresponding to the current section
		// The index should match the section order in the DOM
		if (currentSectionIndex >= 0 && currentSectionIndex < $navItems.length) {
			$navItems.eq(currentSectionIndex).find('a').addClass('active');
		}

		// Cache the current active index to prevent unnecessary updates
		lastActiveNavIndex = currentSectionIndex;
	}


	/**
	 * Main function that handles background color changes based on scroll position
	 * This is called on every scroll event (throttled for performance)
	 */
	function updateBackground() {
		// Get current scroll position (cross-browser compatible)
		const scrollY = window.scrollY || window.pageYOffset;
		const $contentImpact = $('.content-impact');
		const $sections = $('.content-impact > .impact-section');

		// Early return if no content sections found
		if (!$contentImpact.length) return;

		// Calculate viewport boundaries for section visibility detection
		const contentTop = $contentImpact.offset().top;
		const windowHeight = $(window).height();
		const viewportTop = scrollY;
		const viewportBottom = scrollY + windowHeight;

		// Performance optimization: cache section positions to avoid repeated DOM queries
		// This prevents expensive offset() calculations on every scroll event
		// @note: This may break if lazy loading doesn't block space
		const sectionPositions = [];
		$sections.each(function(index) {
			const $section = $(this);
			const sectionTop = $section.offset().top;
			const sectionBottom = sectionTop + $section.outerHeight();
			sectionPositions[index] = { top: sectionTop, bottom: sectionBottom };
		});

		// Don't start crossfade until teh content area is in view
		// This prevents background changes before the user reaches the impact sections
		if (scrollY < contentTop) return;

		// Find which sections are currently visible using cached positions
		// We need to identify both the current section and the next section for crossfading
		let currentSectionIndex = -1;  // Index of the main section currently in view
		let nextSectionIndex = -1;     // Index of the next section starting to appear

		// Loop through all sections to determine visibility
		for (let index = 0; index < sectionPositions.length; index++) {
			const { top: sectionTop, bottom: sectionBottom } = sectionPositions[index];

			// Check if this section is currently visible in the viewport
			// A section is visible if any part of it is within the viewport boundaries
			if (sectionBottom > viewportTop && sectionTop < viewportBottom) {
				// Only set the first visible section as current (topmost section)
				if (currentSectionIndex === -1) {
					currentSectionIndex = index;
				}
			}

			// Check if next section is starting to appear at the bottom of viewport
			// This happens when the next section's top edge enters the viewport
			if (sectionTop <= viewportBottom && sectionTop > viewportTop) {
				// Only consider it the "next" section if it comes after the current section
				if (nextSectionIndex === -1 && index > currentSectionIndex) {
					nextSectionIndex = index;
				}
			}
		}

		// Initialize color variables for the crossfade calculation
		let currentColor, nextColor, crossfadeFactor = 0;

		if (currentSectionIndex >= 0) {
			// Get the color for the current section (using modulo to cycle through 4 colors)
			currentColor = colors[currentSectionIndex % 4];

			if (nextSectionIndex >= 0) {
				// Next section is appearing
				const nextSectionTop = sectionPositions[nextSectionIndex].top;

				// Calculate how much of the next section is visible as a percentage (0 to 1)
				// This determines how far the next section has scrolled into the viewport
				const nextSectionVisibility = Math.max(0, Math.min(1, (viewportBottom - nextSectionTop) / windowHeight));

				// Only start crossfade when next section has reached 33% up the viewport
				// This creates a more natural transition timing
				if (nextSectionVisibility >= 0.33) {
					// Calculate crossfade factor from 33% to 100% visibility
					// This creates a smooth transition over the remaining 67% of the viewport
					const crossfadeStart = 0.33;
					const crossfadeProgress = (nextSectionVisibility - crossfadeStart) / (1 - crossfadeStart);
					crossfadeFactor = Math.min(1, crossfadeProgress);

					// Get the color for the next section
					nextColor = colors[nextSectionIndex % 4];
				} else {
					// Next section not yet at 33% - no crossfade, use current color
					nextColor = currentColor;
					crossfadeFactor = 0;
				}
			} else {
				// No next section visible - use solid current color
				nextColor = currentColor;
				crossfadeFactor = 0;
			}
		} else {
			// No sections visible - fallback to first color
			currentColor = colors[0];
			nextColor = currentColor;
			crossfadeFactor = 0;
		}

		// Apply the calculated color to the background (only if it has changed)
		let newColor;
		if (crossfadeFactor > 0) {
			// Interpolate between current and next color using linear interpolation
			// This creates a smooth transition between the two colors
			// Formula: result = start + (end - start) * factor
			const r = Math.round(currentColor.r + (nextColor.r - currentColor.r) * crossfadeFactor);
			const g = Math.round(currentColor.g + (nextColor.g - currentColor.g) * crossfadeFactor);
			const b = Math.round(currentColor.b + (nextColor.b - currentColor.b) * crossfadeFactor);
			newColor = `rgb(${r}, ${g}, ${b})`;
		} else {
			// No crossfade needed - use solid current color
			newColor = `rgb(${currentColor.r}, ${currentColor.g}, ${currentColor.b})`;
		}

		// Only update the DOM if the color has actually changed
		if (newColor !== lastAppliedColor) {
			$entryContent.css('background-color', newColor);
			lastAppliedColor = newColor;
		}

		// Update navigation menu to highlight the current section
		updateNavigationActiveState(currentSectionIndex);
	}

	// Bind scroll event with throttling for optimal performance
	// This prevents the function from running too frequently and causing performance issues
	$(window).on('scroll', () => {
		if (!ticking) {
			// Use requestAnimationFrame to ensure smooth 60fps updates
			// This runs the function at the optimal time for browser rendering
			requestAnimationFrame(() => {
				updateBackground();
				ticking = false;
			});
			ticking = true;
		}
	});

	// Initial call to set the correct background color on page load
	updateBackground();
}

/**
 * Initialize sticky navigation functionality
 *
 * This function makes the navigation menu stick to the top of the viewport when scrolling,
 * and adjusts the content padding to prevent overlap. It also handles the transition
 * when the navigation reaches the bottom of the content area.
 *
 * @param {jQuery} $ - jQuery reference (passed from WordPress)
 * @param {jQuery} $navElement - The navigation element to make sticky
 * @param {jQuery} $contentElement - The content element to adjust padding for
 */
function initStickyNavigation($, $navElement, $contentElement) {
	// Early return if required elements are not found
	if (!$navElement.length || !$contentElement.length) {
		return;
	}

	// Store initial nav offset for reference
	// This is used to determine when the navigation should become sticky
	let initialNavOffset = $navElement.offset().top;

	// Function to update content padding based on nav width
	// This is needed only when nav uses position: absolute.
	// hacktastic
	const updateContentPadding = function() {
		const navWidth = $navElement[0].offsetWidth;
		$contentElement[0].style.paddingLeft = navWidth + 'px';
	};

	// Performance optimization: throttle scroll events to prevent excessive function calls
	let ticking = false;

	/**
	 * Main function that handles sticky navigation behavior
	 * This is called on every scroll event (throttled for performance)
	 */
	function updateStickyNavigation() {
		const scrollTop = $(window).scrollTop();

		// Performance optimization: batch DOM queries to minimize layout thrashing
		// This prevents multiple reflows by getting all measurements at once
		// IMPORTANT on positioning:
		// - offset()/outerHeight() yield values relative tot eh document (to the page origin)
		// - When we later set $navElement { position: 'absolute', top: ... }, the 'top' value
		//   must be relative to the parent container element.
		// To avoid drama, compute content bounds relative to the document first and
		// convert to conteiner-relative right before applying.
		const contentTop = $contentElement.offset().top;
		const contentBottom = contentTop + $contentElement.outerHeight();
		const visibleNavHeight = $navElement[0].offsetHeight;
		const fixedNavBottom = scrollTop + visibleNavHeight;
		const $offsetParent = $navElement.offsetParent();
		// Get the offset parent's top position relative to the document. This is needed to convert
		// document-relative offsets into that relative to the parent container when
		// applying position: absolute. The defensive checks ensure we don't error if:
		// - offsetParent() returns null (nav is not in DOM or has no positioned ancestor)
		// - offset() is not available (edge case with detached elements)
		// Default to 0 if any check fails, which means the offset parent is the document body.
		const offsetParentTop = $offsetParent && $offsetParent.offset ? ($offsetParent.offset() ? $offsetParent.offset().top : 0) : 0;

		// Determine if navigation should be sticky
		// Two conditions must be met:
		// 1. User has scrolled past the original nav position (nav hit top)
		// 2. Bottom of the sticky nav hasn't reached the bottom of the content
		// When the user has scrolled past the nav's original position AND teh
		// bottom of the nav has NOT yet reached the bottom of the container,
		// we enter the "sticky" phase: pin the nav to the top using position: sticky (top: 0).
		// Using sticky instead of fixed avoids Safari's stupid fixed position bug.
		const shouldBeSticky = scrollTop >= initialNavOffset && fixedNavBottom < contentBottom;

		if (shouldBeSticky) {
			// Pin the navigation to the top using sticky positioning
			// Sticky so no padding compensation needed
			$navElement.css({
				'position': 'sticky',
				'top': '0',
				'z-index': '1000'
			});
			// Remove padding since sticky is in flow
			$contentElement.css('padding-left', '');

		} else if (scrollTop >= initialNavOffset) {
			// Navigation has hit the top but should release - position it at the bottom of content
			// Switch to absolute positioning to freeze the nav at the content bottom
			// Absolute removes nav from flow, so padding compensation is required
			const releasePosition = contentBottom - visibleNavHeight; // document-space (page origin)
			// Convert to parent container offset before applying to CSS 'top'.
			// If we applied the document-relative value directly, the nav would align relative to the page
			// (appearing too low at page end). Subtracting offsetParentTop converts it to the local
			// space of the offset parent, which is what 'top' expects for absolutely positioned elements.
			const releaseTopRelative = releasePosition - offsetParentTop;
			$navElement.css({
				'position': 'absolute',
				'top': releaseTopRelative + 'px',
				'z-index': '1000'
			});
			// absolute, so add padding to compensate for nav being removed from flow
			updateContentPadding();

		} else {
			// Remove sticky positioning - return to normal flow
			$navElement.css({
				'position': '',
				'top': '',
				'z-index': ''
			});
			// Remove padding since nav is back in normal flow
			$contentElement.css('padding-left', '');
		}

		ticking = false;
	}

	// Handle scroll events with throttling for optimal performance
	// This prevents the function from running too frequently and causing performance issues
	$(window).on('scroll', function() {
		if (!ticking) {
			// Use requestAnimationFrame to ensure smooth 60fps updates
			requestAnimationFrame(updateStickyNavigation);
			ticking = true;
		}
	});

	// Handle window resize to recalculate sticky navigation state
	// This ensures the navigation positioning is correct after layout changes
	$(window).on('resize', function() {
		// Make the breakpoint a variable, for easy change; must be changed both here and in
		// menu-mobile.js
		const breakpoint = 1200;

		// Only run sticky navigation logic on desktop view
		// This prevents performance issues on mobile devices
		if (window.innerWidth >= breakpoint) {
			// Recalculate the initial nav offset in case the layout changed
			// This is crucial when switching from mobile to desktop view
			initialNavOffset = $navElement.offset().top;

			// Reset navigation positioning to normal flow first
			$navElement.css({
				'position': '',
				'top': '',
				'z-index': ''
			});
			$contentElement.css('padding-left', '');

			// Recalculate sticky state after resize
			updateStickyNavigation();
		} else {
			// Clean up desktop navigation state when switching to mobile
			$navElement.css({
				'position': '',
				'top': '',
				'z-index': ''
			});
			$contentElement.css('padding-left', '');
		}
	});

	// Initial check for sticky state on page load
	// This handles cases where the page loads already scrolled (e.g., direct links, browser back button)
	const initialScrollTop = $(window).scrollTop();
	const initialContentTop = $contentElement.offset().top;
	const initialContentBottom = initialContentTop + $contentElement.outerHeight();
	const initialVisibleNavHeight = $navElement.outerHeight();
	const initialFixedNavBottom = initialScrollTop + initialVisibleNavHeight;
	const initialShouldBeSticky = initialScrollTop >= initialNavOffset && initialFixedNavBottom < initialContentBottom;


	if (initialShouldBeSticky) {
		// Pin the navigation to the top using sticky positioning
		// This avoids Safari's stupid fixed positioning bug
		// Sticky so no padding compensation needed
		$navElement.css({
			'position': 'sticky',
			'top': '0',
			'z-index': '1000'
		});
		$contentElement.css('padding-left', '');
	} else if (initialScrollTop >= initialNavOffset) {
		// Navigation has hit the top but should release - position it at the bottom of content
		const $initialOffsetParent = $navElement.offsetParent();
		const initialOffsetParentTop = $initialOffsetParent && $initialOffsetParent.offset ? ($initialOffsetParent.offset() ? $initialOffsetParent.offset().top : 0) : 0;
		const releasePosition = initialContentBottom - initialVisibleNavHeight; // document-space
		const releaseTopRelative = releasePosition - initialOffsetParentTop; // offset-parent space
		$navElement.css({
			'position': 'absolute',
			'top': releaseTopRelative + 'px',
			'z-index': '1000'
		});
		// Absolute position, so add padding to compensate for nav being removed from flow
		updateContentPadding();
	}
}

/**
 * Initialize smooth scroll functionality for navigation menu items
 *
 * This function adds smooth scroll behavior to navigation menu links,
 * using a custom easing function with a fixed speed of 1vh per 0.5s.
 *
 * @param {jQuery} $ - jQuery reference (passed from WordPress)
 */
function initSmoothScroll($) {
	// Find all navigation menu links that have anchor hrefs
	const $navLinks = $('#nav-impact .sub-menu a[href^="#"]');

	if (!$navLinks.length) return;

	/**
	 * Calculate the distance to scroll based on viewport height
	 * Uses 1vh per 0.5s as the base speed, then calculates total duration
	 *
	 * @param {number} distance - Distance in pixels to scroll
	 * @returns {number} Duration in milliseconds
	 */
	function calculateDuration(distance) {
		// 1vh per 0.5s = 2vh per 1s
		// Convert distance to vh units, then calculate duration
		const vh = window.innerHeight;
		const distanceInVh = Math.abs(distance) / vh;
		return (distanceInVh / 2) * 1000; // Convert to milliseconds
	}

	/**
	 * Ease-in-out timing function. Found online https://easings.net/#easeInOutQuad.
	 *
	 * @param {number} t - Time progress (0 to 1)
	 * @returns {number} Easing value (0 to 1)
	 */
	function easeInOut(t) {
		return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
	}

	/**
	 * Smooth scroll animation to target element
	 *
	 * @param {jQuery} $target - Target element to scroll to
	 */
	function smoothScrollTo($target) {
		if (!$target.length) return;

		const targetOffset = $target.offset().top;
		const currentScroll = $(window).scrollTop();
		const distance = targetOffset - currentScroll;

		// Don't animate if already at target or very close
		if (Math.abs(distance) < 5) return;

		const duration = calculateDuration(distance);
		const startTime = performance.now();

		/**
		 * Animation frame function for smooth scrolling
		 */
		function animateScroll(currentTime) {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / duration, 1);
			const easedProgress = easeInOut(progress);

			const newScrollTop = currentScroll + (distance * easedProgress);
			$(window).scrollTop(newScrollTop);

			// Continue animation if not complete
			if (progress < 1) {
				requestAnimationFrame(animateScroll);
			}
		}

		// Start the animation
		requestAnimationFrame(animateScroll);
	}

	// Add click handlers to all navigation links
	$navLinks.on('click', function(e) {
		e.preventDefault(); // Prevent default anchor behavior

		const href = $(this).attr('href');
		if (!href || !href.startsWith('#')) return;

		const targetId = href.substring(1);
		const $target = $('#' + targetId);

		if ($target.length) {
			smoothScrollTo($target);
		}
	});
}

// WordPress jQuery document ready function
// This ensures all DOM elements are loaded before initializing our functionality
jQuery( document ).ready(function($) {


	// Initialize sticky navigation after DOM and CSS are ready
	// Using requestAnimationFrame ensures all CSS is applied before measuring elements
	requestAnimationFrame(function() {
		// Initialize sticky navigation functionality
		initStickyNavigation($, $('#nav-impact'), $('.content-impact'));
		// Initialize background crossfade functionality
		initBackgroundCrossfade($, $('.content-impact-outer-wrap'));
		// Initialize smooth scroll functionality
		initSmoothScroll($);
	});

	// Add other functions here as needed
});
